/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import {
  ActionIcon,
  Button,
  Pagination,
  Select,
  Skeleton,
  Text,
  Modal,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Eye, Plus, SortAscending, SortDescending } from "tabler-icons-react";
import { formatISO } from "date-fns";
import { getSignedAgreementCSV } from "../../utilities/apis/agreements";
import {
  loadingStates,
  csvDownload,
  formatDate,
  formatIndianNumber,
} from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import AgreementAnalytics1 from "../../components/AgreementAnalytics/AgreementAnalytics1";
import AgreementGraph1 from "./AgreementGraph1";
import { getAgreementsInSignedList } from "../../utilities/apis/agreements";
import HamburgerMenuAgreements from "../HamburgerMenuAgreements";
import AgreementGraph from "./AgreementGraph";
import { getBussinessUnit } from "../../utilities/apis/serviceProvider";
import RelatedAgreementModal from "./agreementRelatedModal";
import PartyNameFilter, {
  PARTY_NAME_LIST_TYPES,
} from "../../components/PartyNameFilter";
import AgreementTypeFilter from "../../components/AgreementTypeFilter";

const estampOption = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const INITIAL_FILTERS = {
  typeOfAgreement: null,
  mapBusinessunit: null,
  startDate: null,
  endDate: null,
  estamp: null,
  partyName: "",
};

const AgreementsSigned = () => {
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
  const [filters, setFilter] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [showModalRelated, setShowModalRelated] = useState(false);
  const [relatedAgreementId, setRelatedAgreementId] = useState(null);

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [pageSize, setPageSize] = useState(10);
  const [reviewAgreementpage, setReviewAgreementpage] = useState(1);
  const [sortByOptions, setSortByOptions] = useState({
    key: "createdAt",
    value: "desc",
  });
  const [configs, setConfigs] = useState({
    loading: loadingStates.LOADING,
    data: null,
  });
  const [exportButtonLoading, setExportButtonLoading] = useState(false);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const { data } = await getBussinessUnit();
        if (data?.result) {
          setBusinessUnitOptions(
            data.result.map((unit) => ({
              value: String(unit.id),
              label: unit.name,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching business units:", error);
      }
    };
    fetchBusinessUnits();
  }, []);

  const handleFilterChange = (key) => (val) =>
    setFilter((prev) => ({ ...prev, [key]: val }));

  const buildFilterOptions = useCallback((filterState) => {
    if (
      (filterState.startDate && !filterState.endDate) ||
      (!filterState.startDate && filterState.endDate)
    ) {
      showNotification({
        title: "Agreement notification",
        message: "Please select both start and end dates",
        color: "red",
      });
      return null;
    }

    const filterOptions = {};

    if (filterState.startDate && filterState.endDate) {
      filterOptions.createdAt = {
        gte: formatISO(filterState.startDate),
        lte: formatISO(filterState.endDate),
      };
    }
    if (filterState.typeOfAgreement) {
      filterOptions.typeOfAgreement = { equals: filterState.typeOfAgreement };
    }
    if (filterState.estamp) {
      filterOptions.agreementType = {
        equals: filterState.estamp === "Yes" ? "eStamp" : "normal",
      };
    }
    if (filterState.mapBusinessunit) {
      filterOptions.mapBusinessunit = { equals: filterState.mapBusinessunit };
    }
    if (filterState.partyName?.length > 0) {
      filterOptions.OR = [
        {
          secondParty: {
            path: "$.name",
            string_contains: filterState.partyName,
          },
        },
        {
          secondParty: {
            path: "$.indi_name",
            string_contains: filterState.partyName,
          },
        },
        {
          secondParty: {
            path: "$.brandName",
            string_contains: filterState.partyName,
          },
        },
      ];
    }

    return filterOptions;
  }, []);

  const handlePlusClick = (id) => {
    setShowModalRelated(true);
    setRelatedAgreementId(id);
  };

  const handleSearch = () => {
    if (buildFilterOptions(filters) === null) return;
    setAppliedFilters({ ...filters });
    setReviewAgreementpage(1);
  };

  const clearHandler = () => {
    setFilter(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setReviewAgreementpage(1);
  };

  const handleKeyChange = (val) => {
    setSortByOptions((prev) => ({ key: val, value: prev.value }));
  };

  const toggleOrder = () => {
    setSortByOptions((prev) => ({
      key: prev.key,
      value: prev.value === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setConfigs((prev) => ({ ...prev, loading: loadingStates.LOADING }));

        const filterOptions = buildFilterOptions(appliedFilters);
        if (filterOptions === null) {
          setConfigs((prev) => ({
            ...prev,
            loading: loadingStates.NO_ACTIVE_REQUEST,
          }));
          return;
        }

        const sortOption = { [sortByOptions.key]: sortByOptions.value };
        const response = await apiWrapWithErrorWithData(
          getAgreementsInSignedList({
            page: reviewAgreementpage,
            pageSize,
            filterOptions,
            year,
            sortByOptions: sortOption,
          }),
        );

        if (response) {
          setConfigs({
            loading: loadingStates.NO_ACTIVE_REQUEST,
            data: response,
          });
        }
      } catch (error) {
        console.error("Error fetching data: ", error);
        setConfigs((prev) => ({
          ...prev,
          loading: loadingStates.NO_ACTIVE_REQUEST,
        }));
      }
    };
    fetchData();
  }, [
    reviewAgreementpage,
    pageSize,
    year,
    sortByOptions,
    appliedFilters,
    buildFilterOptions,
  ]);

  const handleCsvDownload = async () => {
    const filterOptions = buildFilterOptions(appliedFilters);
    if (filterOptions === null) return;

    try {
      setExportButtonLoading(true);
      const response = await apiWrapWithErrorWithData(
        getSignedAgreementCSV({
          filterOptions,
          sortByOptions: { [sortByOptions.key]: sortByOptions.value },
        }),
      );
      if (response?.success && response?.agreements?.length > 0) {
        csvDownload({
          data: response.agreements,
          headers: Object.keys(response.agreements[0]),
          name: "agreements signed",
        });
      } else {
        showNotification({
          title: "Export",
          message: "No agreements available for export",
          color: "red",
        });
      }
    } catch {
      showNotification({
        title: "Export",
        message: "Could not export CSV",
        color: "red",
      });
    } finally {
      setExportButtonLoading(false);
    }
  };

  const isLoading = configs.loading === loadingStates.LOADING;
  const agreements = configs.data?.agreements || [];
  const hasAgreements = agreements.length > 0;
  let serialNumber = (reviewAgreementpage - 1) * pageSize + 1;

  return (
    <>
      <Modal
        overflow="inside"
        opened={showModalRelated}
        onClose={() => setShowModalRelated(false)}
        size="calc(80vw)"
      >
        <RelatedAgreementModal agreementId={relatedAgreementId} />
      </Modal>

      <div className="mb-20 w-full space-y-6">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-['Lexend'] text-2xl font-semibold tracking-wide text-slate-900">
              Agreements Signed
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              View and filter completed signed agreements
            </p>
          </div>
          <HamburgerMenuAgreements />
        </div>

        {!isLoading && configs.data && (
          <>
            <AgreementAnalytics1
              filter={filters}
              setFilter={setFilter}
              configs={configs}
            />
            <div className="grid grid-cols-2 gap-4">
              <AgreementGraph year={year} setYear={setYear} configs={configs} />
              <AgreementGraph1 configs={configs} />
            </div>
          </>
        )}

        <div className="space-y-4">
          <div className="mb-2 grid grid-cols-4 gap-3">
            <Select
              placeholder="eStamp"
              value={filters.estamp}
              onChange={handleFilterChange("estamp")}
              data={estampOption}
              clearable
            />
            <AgreementTypeFilter
              value={filters.typeOfAgreement}
              onChange={handleFilterChange("typeOfAgreement")}
            />
            <Select
              placeholder="Business unit"
              value={filters.mapBusinessunit}
              data={businessUnitOptions}
              onChange={handleFilterChange("mapBusinessunit")}
              clearable
            />
            <DatePicker
              placeholder="Created date (from)"
              value={filters.startDate}
              onChange={handleFilterChange("startDate")}
              minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
            />
            <DatePicker
              placeholder="Created date (to)"
              value={filters.endDate}
              onChange={handleFilterChange("endDate")}
              minDate={filters.startDate}
              disabled={!filters.startDate}
            />
            <PartyNameFilter
              value={filters.partyName}
              onChange={handleFilterChange("partyName")}
              listType={PARTY_NAME_LIST_TYPES.SIGNED}
            />
          </div>

          <div className="my-4 flex flex-wrap items-center gap-2">
            <Button color="green" onClick={handleSearch}>
              Search
            </Button>
            <Button color="red" onClick={clearHandler}>
              Clear
            </Button>
          </div>

          <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Signed Agreements
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                className="min-w-[160px]"
                value={sortByOptions.key}
                onChange={handleKeyChange}
                data={[
                  { label: "Created at", value: "createdAt" },
                  { label: "End date", value: "dueDateOfContract" },
                  { label: "Type", value: "typeOfAgreement" },
                  { label: "AR NO.", value: "id" },
                ]}
              />
              <ActionIcon
                className="h-10 w-10 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:border-[#46BDE1] hover:bg-[#46BDE1]/10 hover:text-[#46BDE1]"
                onClick={toggleOrder}
              >
                {sortByOptions.value === "asc" ? (
                  <SortAscending size={22} />
                ) : (
                  <SortDescending size={22} />
                )}
              </ActionIcon>
              <Button
                className="bg-[#46BDE1] hover:bg-[#3aa8ca]"
                disabled={exportButtonLoading}
                onClick={handleCsvDownload}
              >
                Export
              </Button>
            </div>
          </div>

          {isLoading && (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              {Array.from({ length: 10 }).map((i) => (
                <Skeleton key={i} height={36} radius="md" />
              ))}
            </div>
          )}

          {!isLoading && hasAgreements && (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="whitespace-nowrap px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Sr no.
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        AR NO.
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Business unit
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Type
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Second party
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Contract date
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        End date
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Related
                      </th>
                      <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                        View
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {agreements.map((row) => (
                      <tr
                        key={row.id}
                        className="transition-colors hover:bg-slate-50/80"
                      >
                        <td className="whitespace-nowrap px-2 py-3 text-slate-500">
                          {serialNumber++}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {`AR-${row.id}`}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                          {row.agreementData?.json?.location || "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                          {row.agreementData?.json?.typeOfAgreement}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="font-medium">
                            {row.agreementData?.json?.brandName}
                          </div>
                          <div className="text-xs text-slate-500">
                            {`(${row.agreementData?.json?.secondParty?.name || "-"})`}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                          {formatDate(
                            row.dateOfContract || row.contractStartDate,
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                          {formatDate(
                            row.dueDateOfContract || row.contractEndDate,
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {row.relationshipWith ? (
                            <ActionIcon
                              className="mx-auto h-9 w-9 rounded-lg border border-slate-200 bg-white text-[#46BDE1] hover:border-[#46BDE1] hover:bg-[#46BDE1]/10"
                              onClick={() => handlePlusClick(row.id)}
                            >
                              <Plus size={20} />
                            </ActionIcon>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Link
                            to={`/app/agreements_manager/agreements/details/${row.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-[#46BDE1] hover:border-[#46BDE1] hover:bg-[#46BDE1]/10"
                          >
                            <Eye size={20} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!isLoading && !hasAgreements && (
            <div className="flex min-h-[300px] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm">
              No agreements
            </div>
          )}

          {!isLoading && configs.data?.agreementsCount > 0 && (
            <div className="flex flex-col items-center justify-center gap-3 py-4 sm:flex-row">
              <Pagination
                page={reviewAgreementpage}
                onChange={setReviewAgreementpage}
                total={Math.ceil(configs.data.agreementsCount / pageSize)}
              />
              <Text className="text-sm text-slate-600">Page size</Text>
              <Select
                className="w-20"
                data={["10", "20", "50"]}
                value={`${pageSize}`}
                onChange={(val) => setPageSize(parseInt(val, 10))}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AgreementsSigned;
